import { ValueFormatterParams } from 'ag-grid-community';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import momentTimeZone from 'moment-timezone';
import { DateValue } from 'react-aria';
import { getTimeZone, locale } from 'src/environment';
import { connector } from './constant';

export const getDate = () => momentTimeZone().tz(getTimeZone());
export const getCurrentISODate = () => getDate().format('YYYY-MM-DD HH:mm:ss');

export const createMoment = (date?: string) => {
  return moment(date ? parseDateWithTimezone(date) : getCurrentISODate());
};

export const convertAUDateTimeToNZDateTime = (date: string) => {
  const timeWIthOffset = momentTimeZone.tz(date, 'Australia/Sydney');
  const timeInDesignatedTimeZone = timeWIthOffset.tz('Pacific/Auckland');
  return timeInDesignatedTimeZone.format('YYYY-MM-DD HH:mm:ss');
};

export const parseDateWithTimezone = (date: string) => {
  if (isISOWithTimezone(date)) {
    const timeWIthOffset = momentTimeZone.parseZone(date);
    const timeInDesignatedTimeZone = timeWIthOffset.tz(getTimeZone());
    return timeInDesignatedTimeZone.format('YYYY-MM-DD HH:mm:ss');
  }
  if (getTimeZone() === 'Pacific/Auckland') {
    return convertAUDateTimeToNZDateTime(date);
  }
  return date;
};
export default function dateFormatter(params: ValueFormatterParams) {
  if (params.node.group && !params.value) {
    return null;
  }
  if (params.node.rowPinned === 'bottom') {
    return params?.value as string;
  }
  const date = params?.value as string;
  return getLocaleDateString(date);
}

function isISOWithTimezone(str) {
  const isoWithTZRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?([+-]\d{2}:\d{2}|Z)$/;
  return isoWithTZRegex.test(str);
}

export const getLocaleDateString = (date, options?: any) => {
  const formatOptions = options ? options : { year: 'numeric', month: '2-digit', day: '2-digit' };
  if (moment.isDate(date)) {
    return date.toLocaleDateString(locale, formatOptions);
  }
  return date ? moment(parseDateWithTimezone(date)).toDate().toLocaleDateString(locale, formatOptions) : '-';
};
export const getLocaleString = (date, options?: Intl.DateTimeFormatOptions) => {
  if (!date) {
    return '';
  }

  const formatOptions: Intl.DateTimeFormatOptions = options ?? {
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  if (moment.isDate(date)) {
    return date.toLocaleString(locale, formatOptions).replace(',', '');
  }
  return date
    ? moment(parseDateWithTimezone(date)).toDate().toLocaleString(locale, formatOptions).replace(',', '')
    : '';
};

export function dateTimeFormatter(params: ValueFormatterParams) {
  if (params.node.rowPinned === 'bottom') {
    return params?.value as string;
  }
  const date = params?.value as string;
  return getLocaleString(date, {
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export const getLocaleTimeString = (date, options = {}) => {
  if (moment.isDate(date)) {
    return date.toLocaleTimeString(locale, options);
  }
  return date ? moment(parseDateWithTimezone(date)).toDate().toLocaleTimeString(locale, options) : '';
};
export const timeFormatter = (params) => {
  if (params.node.rowPinned === 'bottom') {
    return params?.value as string;
  }
  const date = params?.value as string;
  return getLocaleTimeString(date, { hour12: true, hour: 'numeric', minute: '2-digit' });
};

export const nth = (d) => {
  if (!d) {
    return '';
  }
  if (d > 3 && d < 21) return `${d}th`;
  switch (d % 10) {
    case 1:
      return `${d}st`;
    case 2:
      return `${d}nd`;
    case 3:
      return `${d}rd`;
    default:
      return `${d}th`;
  }
};

export const getISODateString = (value?: any) => {
  if (!value) {
    return '';
  }
  if (moment.isMoment(value)) {
    return value.format('YYYY-MM-DD');
  }

  return createMoment(value).format('YYYY-MM-DD');
};

export const autocompleteYear = (value: DateValue) => {
  if (value?.year < 100) {
    const clonedValue = cloneDeep(value);
    const currentYear = getDate().year();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const lastCentury = currentCentury - 100;

    const candidate1 = currentCentury + clonedValue.year;
    const candidate2 = lastCentury + clonedValue.year;

    const closestYear = [candidate1, candidate2].reduce((prev, curr) =>
      Math.abs(curr - currentYear) < Math.abs(prev - currentYear) ? curr : prev,
    );
    return clonedValue.set({ year: closestYear });
  }
  return value;
};

export const dateValueGetterFactory = (key: string) => {
  return (params) => {
    return params?.data?.[key] ? createMoment(params?.data?.[key]).format('YYYY-MM-DD') : '';
  };
};

export const dateComparator = (valueA, valueB, nodeA, nodeB, isDescending) => {
  // Handle null/empty values
  if (!valueA && !valueB) {
    return 0;
  }
  if (!valueA) {
    return -1; // Empty values go to the front
  }
  if (!valueB) {
    return 1; // Empty values go to the front
  }
  const dateA = valueA.split(connector)[0];
  const dateB = valueB.split(connector)[0];
  const momentA = createMoment(dateA);
  const momentB = createMoment(dateB);
  if (momentA.isSame(momentB, 'day')) {
    return 0;
  }
  return momentA.isBefore(momentB, 'day') ? -1 : 1;
};
